/**
 * EPA tract walkability rollups shared by Transportation → Walkability and Livability → Mobility.
 * Mean of tract-level nat_walk_* percentiles for tracts in the selected place (crosswalk) or county.
 */

import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

/** Suppress repeated identical [walkability] console.warn under React Strict Mode. */
let walkabilityPlaceTractWarnDedupe = '';

export type WalkClass =
  | 'Highly walkable (80–100)'
  | 'Above average (60–80)'
  | 'Moderate (40–60)'
  | 'Below average (20–40)'
  | 'Least walkable (0–20)'
  | 'Unknown';

export const CLASS_ORDER: WalkClass[] = [
  'Highly walkable (80–100)',
  'Above average (60–80)',
  'Moderate (40–60)',
  'Below average (20–40)',
  'Least walkable (0–20)',
  'Unknown'
];

/**
 * Census tract GEOIDs are 11-digit strings with significant leading zeros (e.g. 06001400100).
 * Never parse via `Number()` alone — JavaScript drops leading zeros and breaks joins to `relationship_trct_place_20.trct`.
 */
export function normalizeTractGeofips(value: string | number | null | undefined): string {
  if (value == null) return '';
  let digits: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    digits = String(Math.trunc(Math.abs(value)));
  } else {
    digits = String(value).replace(/\D/g, '');
  }
  if (!digits) return '';
  if (digits.length <= 11) return digits.padStart(11, '0');
  return digits.slice(-11);
}

export function countyGeofipsToTractPrefix(countyGeofips: number | null): string | null {
  if (countyGeofips == null || Number.isNaN(countyGeofips)) return null;
  const n = Math.trunc(Math.abs(countyGeofips));
  const s = String(n);
  if (s.length <= 5) return s.padStart(5, '0');
  return s.slice(0, 5);
}

export function classifyFromPercentile(p: number | null | undefined): WalkClass {
  if (p == null || Number.isNaN(Number(p))) return 'Unknown';
  const v = Number(p);
  if (v >= 80) return 'Highly walkable (80–100)';
  if (v >= 60) return 'Above average (60–80)';
  if (v >= 40) return 'Moderate (40–60)';
  if (v >= 20) return 'Below average (20–40)';
  return 'Least walkable (0–20)';
}

export function pickPercentile(row: Record<string, unknown>): number | null {
  const keys = Object.keys(row);
  const preferred = keys.find((k) => /nat_walk.*percentile/i.test(k));
  const key = preferred || keys.find((k) => /walk.*percentile/i.test(k));
  if (!key) return null;
  const v = row[key];
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

export async function fetchCountyWalkabilityRows(countyPrefix: string): Promise<Record<string, unknown>[]> {
  const pageSize = 1000;
  let offset = 0;
  const all: Record<string, unknown>[] = [];
  const pad = countyPrefix.padStart(5, '0');

  while (true) {
    const { data, error } = await supabaseClient
      .from('walkability_tract')
      .select('*')
      .like('geofips', `${pad}%`)
      .range(offset, offset + pageSize - 1);

    if (error) {
      const { data: alt, error: err2 } = await supabaseClient
        .from('walkability_tract')
        .select('*')
        .filter('geofips', 'gte', `${pad}00000`)
        .filter('geofips', 'lte', `${pad}99999`)
        .range(offset, offset + pageSize - 1);
      if (err2) throw error;
      const rows = (alt || []) as Record<string, unknown>[];
      all.push(...rows);
      if (rows.length < pageSize) break;
      offset += pageSize;
      continue;
    }

    const rows = (data || []) as Record<string, unknown>[];
    all.push(...rows);
    if (rows.length < pageSize) break;
    offset += pageSize;
  }

  if (all.length > 0) return all;

  const { data: fallback, error: fbErr } = await supabaseClient.from('walkability_tract').select('*').limit(5000);
  if (fbErr) throw fbErr;
  const rows = (fallback || []) as Record<string, unknown>[];
  return rows.filter((r) => {
    const g = normalizeTractGeofips(r.geofips as string | number | undefined);
    const g11 = g.length === 11 ? g : g.padStart(11, '0');
    return g11.startsWith(pad);
  });
}

/** 11-digit tract GEOIDs from walkability county rows (for tract→place join). */
export function tractGeoidsFromWalkabilityRows(rows: Record<string, unknown>[]): string[] {
  const ids: string[] = [];
  for (const row of rows) {
    const raw = row.geofips ?? (row as { tract?: unknown }).tract ?? (row as { geoid?: unknown }).geoid;
    const g = normalizeTractGeofips(raw as string | number);
    if (!g) continue;
    ids.push(g.length === 11 ? g : g.padStart(11, '0'));
  }
  return ids;
}

function buildPlaceGeofipsVariants(placeGeofips: string): Set<string> {
  const raw = String(placeGeofips).trim();
  const numeric = raw.replace(/\D/g, '');
  return new Set(
    [
      raw,
      numeric,
      numeric.replace(/^0+/, '') || '0',
      numeric.padStart(7, '0'),
      /^\d+$/.test(numeric) ? String(Number.parseInt(numeric, 10)) : '',
    ].filter(Boolean)
  );
}

function stplcMatchesVariant(stplc: unknown, variants: Set<string>): boolean {
  const digits = String(stplc ?? '').replace(/\D/g, '');
  if (!digits) return false;
  const n = Number(digits);
  const pad7 = digits.padStart(7, '0');
  for (const v of Array.from(variants)) {
    const vd = v.replace(/\D/g, '');
    if (!vd) continue;
    if (vd === digits) return true;
    if (Number(vd) === n) return true;
    if (vd.padStart(7, '0') === pad7) return true;
  }
  return false;
}

function placeNameLikelyMatch(stplcname: string, locationName: string): boolean {
  const name = stplcname.trim().toLowerCase();
  const loc = locationName.trim().toLowerCase();
  if (!loc || !name) return false;
  return (
    name.startsWith(`${loc} city`) ||
    name.startsWith(`${loc} cdp`) ||
    name.startsWith(`${loc} town`)
  );
}

async function queryRelationshipChunk(
  table: 'relationship_trct_place_20' | 'relationship_trct_place_10',
  chunk: string[]
): Promise<{ trct?: unknown; stplc?: unknown; stplcname?: unknown }[] | null> {
  type Row = { trct?: unknown; stplc?: unknown; stplcname?: unknown };
  const padded = chunk.map((c) => normalizeTractGeofips(c).padStart(11, '0'));
  const asInts = padded.map((p) => parseInt(p, 10)).filter((n) => Number.isFinite(n));

  const tryKeys = async (keys: (string | number)[]): Promise<Row[] | null> => {
    const r = await supabaseClient.from(table).select('trct, stplc, stplcname').in('trct', keys);
    if (!r.error && r.data?.length) return r.data as Row[];
    if (r.error?.message?.toLowerCase().includes('column')) {
      const rb = await supabaseClient.from(table).select('trct, stplc').in('trct', keys);
      if (!rb.error && rb.data?.length) return rb.data as Row[];
    }
    return null;
  };

  const stringTry = await tryKeys(padded);
  if (stringTry) return stringTry;
  if (asInts.length) {
    const intTry = await tryKeys(asInts);
    if (intTry) return intTry;
  }
  return null;
}

/**
 * Load all tracts for the place FIPS via `stplc IN (...)`, then keep only those in the county walkability set.
 * More reliable than per-variant `eq` + does not depend on `trct` key type matching in `.in('trct', …)`.
 */
async function fetchPlaceTractsStplcInIntersectCounty(
  variants: Set<string>,
  countyTractGeoids: string[],
  addTrctRows: (rows: { trct?: unknown }[] | null | undefined) => void
): Promise<void> {
  const stplcList: (string | number)[] = [];
  for (const v of Array.from(variants)) {
    stplcList.push(v);
    if (/^\d+$/.test(String(v))) stplcList.push(Number(v));
  }
  const stplcIn = Array.from(new Set(stplcList));
  const county = new Set(countyTractGeoids.map((g) => normalizeTractGeofips(g).padStart(11, '0')));

  for (const table of ['relationship_trct_place_20', 'relationship_trct_place_10'] as const) {
    const { data, error } = await supabaseClient.from(table).select('trct, stplc').in('stplc', stplcIn);
    if (error) continue;
    for (const row of data || []) {
      const tr = normalizeTractGeofips((row as { trct?: string | number | null }).trct);
      if (!tr) continue;
      const id = tr.length === 11 ? tr : tr.padStart(11, '0');
      if (county.has(id)) addTrctRows([row]);
    }
  }
}

/**
 * When direct `stplc = geofips` fails (padding/types), join on tracts from walkability_tract for this county.
 */
async function fetchPlaceTractsViaCountyTractJoin(
  variants: Set<string>,
  countyTractGeoids: string[],
  locationName: string | undefined,
  addTrctRows: (rows: { trct?: unknown }[] | null | undefined) => void
): Promise<void> {
  const uniq = Array.from(new Set(countyTractGeoids.map((g) => normalizeTractGeofips(g).padStart(11, '0')))).filter(
    Boolean
  );
  if (!uniq.length) return;

  const chunkSize = 120;
  const tables = ['relationship_trct_place_20', 'relationship_trct_place_10'] as const;

  for (const table of tables) {
    for (let i = 0; i < uniq.length; i += chunkSize) {
      const chunk = uniq.slice(i, i + chunkSize);
      const rows = await queryRelationshipChunk(table, chunk);
      if (!rows?.length) continue;

      for (const row of rows) {
        const stplc = row.stplc;
        const nm = row.stplcname != null ? String(row.stplcname) : '';
        const matchSt = stplcMatchesVariant(stplc, variants);
        const matchNm = !!(locationName && nm && placeNameLikelyMatch(nm, locationName));
        if (matchSt || matchNm) {
          addTrctRows([row]);
        }
      }
    }
  }
}

/**
 * Tracts whose centroid intersects the place per relationship_trct_place_20 / _10.
 * Pass **countyTractGeoids** (from walkability_tract in that county) so we can resolve via tract→place join when `eq(stplc)` is unreliable.
 */
export async function fetchPlaceTractSet(
  placeGeofips: string,
  locationName?: string,
  countyTractGeoids?: string[] | null
): Promise<Set<string>> {
  const out = new Set<string>();
  const variants = buildPlaceGeofipsVariants(placeGeofips);

  const addTrctRows = (rows: { trct?: unknown }[] | null | undefined) => {
    for (const row of rows || []) {
      const tr = row.trct;
      const id = normalizeTractGeofips(tr as string | number);
      if (id) out.add(id.length === 11 ? id : id.padStart(11, '0'));
    }
  };

  const variantList = Array.from(variants);

  for (const pid of variantList) {
    let { data, error } = await supabaseClient
      .from('relationship_trct_place_20')
      .select('trct, stplc')
      .eq('stplc', pid);
    if (!error && data?.length) {
      addTrctRows(data);
      if (out.size > 0) return out;
    }
    if (/^\d+$/.test(String(pid))) {
      const num = Number(pid);
      ({ data, error } = await supabaseClient
        .from('relationship_trct_place_20')
        .select('trct, stplc')
        .eq('stplc', num));
      if (!error && data?.length) {
        addTrctRows(data);
        if (out.size > 0) return out;
      }
    }
  }

  const loc = locationName?.trim();
  if (loc) {
    const patterns = [
      ...['city', 'CDP', 'town'].map((suf) => `${loc} ${suf}, CA%`),
      `${loc} city, California%`,
      `${loc}%, CA`,
    ];
    for (const pattern of patterns) {
      const s1 = await supabaseClient
        .from('relationship_trct_place_20')
        .select('trct, stplc, stplcname')
        .ilike('stplcname', pattern);
      if (!s1.error && s1.data?.length) {
        addTrctRows(s1.data as { trct?: unknown }[]);
        if (out.size > 0) return out;
      }
      if (s1.error?.message?.toLowerCase().includes('column')) {
        const s2 = await supabaseClient.from('relationship_trct_place_20').select('trct, stplc').ilike('stplcname', pattern);
        if (!s2.error && s2.data?.length) {
          addTrctRows(s2.data as { trct?: unknown }[]);
          if (out.size > 0) return out;
        }
      }
    }
  }

  if (out.size === 0 && countyTractGeoids?.length) {
    await fetchPlaceTractsStplcInIntersectCounty(variants, countyTractGeoids, addTrctRows);
  }

  if (out.size === 0 && countyTractGeoids?.length) {
    await fetchPlaceTractsViaCountyTractJoin(variants, countyTractGeoids, loc, addTrctRows);
  }

  if (out.size === 0 && countyTractGeoids?.length) {
    const dedupeKey = `${placeGeofips}|${loc ?? ''}|${countyTractGeoids[0] ?? ''}`;
    if (walkabilityPlaceTractWarnDedupe !== dedupeKey) {
      walkabilityPlaceTractWarnDedupe = dedupeKey;
      console.warn(
        '[walkability] No place tracts matched. Check relationship_trct_place_20 RLS/data and population.geofips vs stplc.',
        {
          placeGeofips,
          locationName: loc,
          countyTractSample: countyTractGeoids.slice(0, 3).map((g) => normalizeTractGeofips(g)),
        }
      );
    }
  }

  return out;
}

export function aggregateWalkabilityRows(
  countyRows: Record<string, unknown>[],
  placeTractIds: Set<string> | null
): {
  counts: Record<WalkClass, number>;
  avgPercentile: number | null;
  pctHighlyWalkableTracts: number | null;
  tractCount: number;
} {
  const emptyCounts = (): Record<WalkClass, number> => ({
    'Highly walkable (80–100)': 0,
    'Above average (60–80)': 0,
    'Moderate (40–60)': 0,
    'Below average (20–40)': 0,
    'Least walkable (0–20)': 0,
    Unknown: 0
  });

  const run = (filterByPlace: boolean) => {
    const counts = emptyCounts();
    let sumPct = 0;
    let nPct = 0;
    for (const row of countyRows) {
      const raw = row.geofips ?? (row as { tract?: unknown }).tract ?? (row as { geoid?: unknown }).geoid;
      const g = normalizeTractGeofips(raw as string | number);
      const geoid = g.length === 11 ? g : g.padStart(11, '0');
      if (filterByPlace && placeTractIds && !placeTractIds.has(geoid)) continue;
      const pct = pickPercentile(row);
      const k = classifyFromPercentile(pct);
      counts[k] += 1;
      if (pct != null && !Number.isNaN(Number(pct))) {
        sumPct += Number(pct);
        nPct += 1;
      }
    }
    return { counts, sumPct, nPct };
  };

  /** null = county scope; Set (even empty) = city scope — do not fall back to all county tracts. */
  const filterToPlace = placeTractIds != null;
  let { counts, sumPct, nPct } = run(filterToPlace);

  const denom = CLASS_ORDER.filter((c) => c !== 'Unknown').reduce((acc, c) => acc + counts[c], 0);

  return {
    counts,
    avgPercentile: nPct > 0 ? sumPct / nPct : null,
    pctHighlyWalkableTracts: denom > 0 ? (counts['Highly walkable (80–100)'] / denom) * 100 : null,
    tractCount: denom
  };
}

export async function resolveCountyPrefixForLocation(
  location: string
): Promise<{ countyPrefix: string | null; scope: 'city' | 'county' | 'state' | 'unknown'; placeGeofips: string | null }> {
  await supabaseLocationService.initialize();
  const trimmed = location.trim();
  if (!trimmed) return { countyPrefix: null, scope: 'unknown', placeGeofips: null };

  if (trimmed.toLowerCase() === 'california') {
    return { countyPrefix: null, scope: 'state', placeGeofips: null };
  }

  const locType = supabaseLocationService.getLocationType(trimmed);

  if (locType === 'county') {
    const gf = await supabaseLocationService.getGeofips(trimmed);
    return { countyPrefix: countyGeofipsToTractPrefix(gf), scope: 'county', placeGeofips: null };
  }

  if (locType === 'city') {
    const placeGf = await supabaseLocationService.getGeofips(trimmed);
    const countyName = supabaseLocationService.getCountyForCity(trimmed);
    if (!countyName) {
      return { countyPrefix: null, scope: 'city', placeGeofips: placeGf != null ? String(placeGf) : null };
    }
    const { data: countyRow } = await supabaseClient
      .from('population')
      .select('geofips')
      .eq('geography_type', 'County')
      .ilike('location_name', `${countyName}%`)
      .limit(1)
      .maybeSingle();
    const countyGf = countyRow?.geofips != null ? Number(countyRow.geofips) : null;
    return {
      countyPrefix: countyGeofipsToTractPrefix(countyGf),
      scope: 'city',
      placeGeofips: placeGf != null ? String(placeGf) : null
    };
  }

  const gf = await supabaseLocationService.getGeofips(trimmed);
  const prefix = countyGeofipsToTractPrefix(gf);
  return { countyPrefix: prefix, scope: 'unknown', placeGeofips: gf != null ? String(gf) : null };
}

/** Rollup for Transportation Walkability + Livability Mobility (same tract scope and aggregation). */
export type WalkabilityPlaceMetrics = {
  avgPercentile: number | null;
  tractCount: number;
  pctHighlyWalkableTracts: number | null;
  counts: Record<WalkClass, number>;
  scope: 'city' | 'county' | 'state' | 'unknown';
};

export async function fetchWalkabilityPlaceMetrics(location: string): Promise<WalkabilityPlaceMetrics | null> {
  const { countyPrefix, scope, placeGeofips } = await resolveCountyPrefixForLocation(location);
  if (scope === 'state' || !countyPrefix) return null;

  const countyRows = await fetchCountyWalkabilityRows(countyPrefix);
  let placeTractIds: Set<string> | null = null;
  if (supabaseLocationService.getLocationType(location.trim()) === 'city' && placeGeofips) {
    placeTractIds = await fetchPlaceTractSet(placeGeofips, location.trim(), tractGeoidsFromWalkabilityRows(countyRows));
  }
  const agg = aggregateWalkabilityRows(countyRows, placeTractIds);
  return {
    avgPercentile: agg.avgPercentile,
    tractCount: agg.tractCount,
    pctHighlyWalkableTracts: agg.pctHighlyWalkableTracts,
    counts: agg.counts,
    scope,
  };
}

/**
 * Average tract EPA walkability percentile for the location (same scope as Transportation → Walkability).
 * Returns null for state-wide or unresolved county.
 */
export async function fetchWalkabilityAreaAveragePercentile(location: string): Promise<number | null> {
  const m = await fetchWalkabilityPlaceMetrics(location);
  return m?.avgPercentile ?? null;
}
